import { ResizeObserver } from "@juggle/resize-observer";
import {
  CaretDown,
  Globe,
  Info,
  Lock,
  PlusCircle,
  TextT,
} from "@phosphor-icons/react";
import {
  Banner,
  Combobox,
  ComboboxItem,
  ComboboxList,
  ComboboxProvider,
  FormGroup,
  Select,
  SelectItem,
  SelectLabel,
  SelectPopover,
  SelectProvider,
} from "@replicate/ui";
import { matchSorter } from "match-sorter";
import { startTransition, useEffect, useMemo, useState } from "react";
import useDimensions from "react-cool-dimensions";
import { useController } from "react-hook-form";
import { Label } from "../api-playground/label";

const CREATE_ITEM_VALUE = "create_new_model";

export function ModelPicker({
  models,
  owner,
  avatar,
  disabled,
  setFocus,
}: {
  owner: string;
  avatar: string;
  models: { name: string; username: string }[];
  disabled: boolean;
  setFocus: (name: string) => void;
}) {
  const [searchValue, setSearchValue] = useState("");

  const matches: { name: string; username: string }[] = useMemo(() => {
    if (models.length === 0) {
      return [];
    }

    return matchSorter(models, searchValue, {
      keys: ["name"],
    });
  }, [models, searchValue]);

  const { field: destinationField, formState } = useController({
    name: "destination",
    rules: {
      required: {
        value: true,
        message: "This field is required",
      },
      pattern: {
        value: /^[a-z0-9]+(?:(?:[._]|__|[-]+)[a-z0-9]+)*$/,
        message:
          "Enter a valid name. This value may contain only lowercase letters, numbers, dashes, underscores, or periods, and may not start or end with a dash, underscore, or period.",
      },
    },
  });

  const { field: modelVisibilityField } = useController({
    name: "visibility",
    defaultValue: "public",
    rules: {
      required: {
        value: true,
        message: "This field is required",
      },
    },
  });

  const { field: createModelField } = useController({ name: "createModel" });
  const isCreatingModel = createModelField.value;

  const selectDisplayValue = useMemo(() => {
    if (isCreatingModel) {
      return "Creating new model using name below";
    }

    if (destinationField.value) {
      return `${owner}/${destinationField.value}`;
    }

    return "Select a model";
  }, [destinationField.value, owner, isCreatingModel]);

  const visibilityOptions = {
    public: {
      label: "Public",
      description: "Anyone can run this model and see its source code.",
      icon: <Globe />,
    },
    private: {
      label: "Private",
      description: "Only you and collaborators can see this model.",
      icon: <Lock />,
    },
  };

  const selectInfoValue = useMemo(() => {
    if (isCreatingModel) {
      return (
        <div>
          {Object.entries(visibilityOptions).map(([key, value]) => (
            <div key={key}>
              <label
                htmlFor={`visibility_${key}`}
                className="flex px-4 py-2 hover:bg-r8-gray-1 border border-transparent has-[:checked]:border-r8-gray-5"
              >
                <div className="flex-shrink mt-1 mr-lh text-md">
                  {value.icon}
                </div>
                <div className="flex-grow">
                  <p>{value.label}</p>
                  <p className="text-r8-gray-11 text-sm">{value.description}</p>
                </div>
                <input
                  type="radio"
                  className="flex-initial"
                  {...modelVisibilityField}
                  value={key}
                  defaultChecked={key === "public"}
                  id={`visibility_${key}`}
                />
              </label>
            </div>
          ))}
        </div>
      );
    }

    if (destinationField.value) {
      return (
        <Banner
          severity="info"
          description={
            <p>
              The trained model will be added to Replicate as a new version of
              the{" "}
              <code>
                {owner}/{destinationField.value}
              </code>{" "}
              model.
            </p>
          }
          condensed
          icon={<Info />}
        />
      );
    }

    return (
      <p className="text-r8-sm text-r8-gray-12">
        Select a model on Replicate that will be the destination for the trained
        version. If the model does not exist, select the "Create model" option
        and a field will appear to enter the name of the new model. We'll create
        the model for you when you create the training.
      </p>
    );
  }, [destinationField.value, modelVisibilityField, isCreatingModel, owner]);

  const createModelAction = (
    <SelectItem
      key="create-model"
      value={CREATE_ITEM_VALUE}
      className="flex gap-1.5"
      render={<ComboboxItem />}
    >
      <PlusCircle weight="bold" />
      <span>Create new model</span>
    </SelectItem>
  );

  let items = matches.map((value: { name: string; username: string }) => (
    <SelectItem
      key={`${value.username}/${value.name}`}
      value={value.name}
      className="select-item"
      render={
        <ComboboxItem>
          {value.username}/{value.name}
        </ComboboxItem>
      }
    />
  ));

  if (searchValue) {
    items = [...items, createModelAction];
  } else {
    items = [createModelAction, ...items];
  }

  const { observe: observeOwner, width: ownerWidth } = useDimensions({
    polyfill: ResizeObserver,
  });

  const [selectionMade, setSelectionMade] = useState(false);
  useEffect(() => {
    if (selectionMade) {
      setFocus(destinationField.name);
      setSelectionMade(false);
    }
  }, [selectionMade, destinationField.name, setFocus]);

  return (
    <div
      className={`space-y-4 py-4 border ${
        isCreatingModel
          ? "p-4 bg-r8-gray-2 border-r8-gray-6"
          : "border-transparent"
      }`}
    >
      <div className="gap-2 flex flex-col">
        <ComboboxProvider
          resetValueOnHide
          setValue={(value) => {
            startTransition(() => {
              setSearchValue(value);
            });
          }}
        >
          <SelectProvider
            defaultValue={null}
            value={destinationField.value}
            setValue={(val) => {
              if (val === CREATE_ITEM_VALUE) {
                if (
                  matches
                    .map((item) => item.name)
                    .includes(destinationField.value)
                ) {
                  destinationField.onChange("");
                }
                if (!destinationField.value && searchValue) {
                  destinationField.onChange(searchValue);
                }
                createModelField.onChange(true);
                setSelectionMade(true);
              } else {
                destinationField.onChange(val);
                createModelField.onChange(false);
              }
            }}
          >
            <FormGroup id="destination">
              <SelectLabel>
                <Label
                  type="string"
                  Icon={TextT}
                  required={true}
                  name="Destination"
                />
              </SelectLabel>
              <FormGroup.Control
                endAdornment={<CaretDown />}
                endAdornmentOptions={{
                  absolute: true,
                  inert: true,
                }}
                input={
                  <Select
                    disabled={disabled}
                    className={`${
                      (isCreatingModel || !destinationField.value) &&
                      "text-r8-gray-11"
                    }`}
                  >
                    {selectDisplayValue}
                  </Select>
                }
              />
            </FormGroup>
            <SelectPopover
              className="flex flex-col max-h-64"
              gutter={8}
              sameWidth
            >
              <div className="flex-shrink-0">
                <FormGroup size="sm" id="search-models">
                  <FormGroup.Control
                    input={
                      <div className="combobox-wrapper">
                        <Combobox
                          className="bg-r8-gray-3 focus:ring-0"
                          style={{
                            marginLeft: `${ownerWidth + 8}px`,
                          }}
                          autoSelect
                          placeholder="Search models..."
                        />
                        {/* This is rtl, to truncate the owner name on the left hand side, to improve readability */}
                        <div
                          ref={observeOwner}
                          className="pointer-events-none text-right text-ellipsis overflow-hidden max-w-64 absolute top-0 left-0 bottom-0 p-2 text-r8-sm text-r8-gray-11 [direction:rtl]"
                        >
                          <span className="text-r8-gray-8 pl-2">/</span>
                          {owner}
                        </div>
                      </div>
                    }
                  />
                </FormGroup>
              </div>
              <ComboboxList className="flex-1 h-full overflow-auto border-t-2 border-r8-gray-1">
                {items}
              </ComboboxList>
            </SelectPopover>
          </SelectProvider>
        </ComboboxProvider>
      </div>
      {isCreatingModel && (
        <div className="py-2">
          <div className="relative border border-r8-gray-6 flex bg-white dark:bg-r8-gray-1 items-center focus-within:ring focus-within:border-r8-blue-10">
            <div className="flex items-center gap-2 p-2">
              <img
                className="w-6 h-6 rounded-full flex-shrink-0"
                alt={`Avatar for ${owner}`}
                src={avatar}
              />
              <span>{owner}</span>
              <span>/</span>
            </div>
            <input
              onChange={(e) => {
                destinationField.onChange(e.currentTarget.value);
              }}
              name="destination"
              className="flex-1 py-2 pl-0 pr-2 h-full focus:outline-none bg-white dark:bg-r8-gray-1"
              placeholder="my-model-name"
              type="string"
              value={destinationField.value ?? ""}
              ref={destinationField.ref}
            />
          </div>
        </div>
      )}
      {formState.errors?.destination && (
        <div className="mt-2 flex items-center text-r8-red-10 gap-2">
          <Info className="flex-shrink-0" size={16} />
          <p className="text-r8-sm">
            {formState.errors?.destination?.message as string}
          </p>
        </div>
      )}
      <div className="mt-2">
        <div>{selectInfoValue}</div>
      </div>
    </div>
  );
}
